import React from "react";

import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import { Route } from "@mapmycustomers/shared/types/entity";
import { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { openEntity } from "@app/store/entityView/actions";
import { showEntityChannel } from "@app/store/entityView/sagas";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";

import { deleteRoute, duplicateRoute, fetchRoute, fetchRoutePreview, fetchRoutes } from "./actions";
import { routeActionMessages } from "./messages";
import sagaStyles from "./saga.module.scss";

const ROUTES_FILTER_PAGE_LIMIT = 100;

const mergeResponses = (response: ListResponse<Route>, extraResponse?: ListResponse<Route>) => {
  const result = response.data;
  if (extraResponse) {
    const alreadyFetchedRouteIds = new Set(response.data.map(({ id }) => id));
    extraResponse.data.forEach((route) => {
      if (!alreadyFetchedRouteIds.has(route.id)) {
        result.push(route);
      }
    });
  }
  return result.sort(nameComparator);
};

export function* onFetchRoutes({ payload }: ReturnType<typeof fetchRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const query = payload?.query;
    const selectedRouteIds = payload?.selectedRouteIds;

    const [routesResponse, extraRoutesResponse]: [
      ListResponse<Route>,
      ListResponse<Route> | undefined,
    ] = yield all([
      callApi("fetchUnifiedRoutes", orgId, {
        $filters: { name: query ? { $in: query } : undefined },
        $limit: ROUTES_FILTER_PAGE_LIMIT,
        $order: "name",
      }),
      selectedRouteIds?.length
        ? callApi("fetchUnifiedRoutes", orgId, {
            $filters: { $and: [{ id: { $in: selectedRouteIds } }] },
            $limit: selectedRouteIds.length,
          })
        : undefined,
    ]);

    const result: Route[] = yield call(mergeResponses, routesResponse, extraRoutesResponse);
    yield put(fetchRoutes.success(result));
  } catch (error) {
    yield put(fetchRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchRoute({ payload }: ReturnType<typeof fetchRoute.request>) {
  try {
    const { id, callback } = payload;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const route: Route = yield callApi("fetchUnifiedRoute", orgId, id);

    yield call(callback, route);
    yield put(fetchRoute.success());
  } catch (error) {
    yield put(fetchRoute.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchRoutePreview({
  payload: { callback, failureCallback, payload },
}: ReturnType<typeof fetchRoutePreview.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const route: RoutePreview = yield callApi("previewUnifiedRoute", orgId, payload);

    if (callback) {
      yield call(callback, route);
    }
    yield put(fetchRoutePreview.success());
  } catch (error) {
    yield put(fetchRoutePreview.failure());
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* onDeleteRoute({
  payload: { callback, routeId },
}: ReturnType<typeof deleteRoute.request>) {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();

  try {
    yield callApi("deleteUnifiedRoute", orgId, routeId);
    notification.success({ message: intl?.formatMessage(routeActionMessages.deleteSuccess) });
    yield put(deleteRoute.success());
    yield put(notifyAboutChanges({ deletedIds: [routeId], entityType: EntityType.ROUTE }));
    // The caller decides what "done" means — each list refreshes its own page.
    yield call(callback);
  } catch (error) {
    yield put(deleteRoute.failure(error));
    yield put(handleError({ defaultMessage: routeActionMessages.deleteFailure, error }));
  }
}

export function* onDuplicateRoute({
  payload: { callback, routeId },
}: ReturnType<typeof duplicateRoute.request>) {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `duplicateRoute_${routeId}`;

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(routeActionMessages.duplicatePending),
  });

  try {
    const route: Route = yield callApi("cloneUnifiedRoute", orgId, routeId);
    // Keep the user where they are and let the caller refresh rather than
    // navigating to the clone — the copy may land on another page, so the success
    // toast carries a "View" action for one-click access instead.
    notification.success({
      duration: NOTIFICATION_DURATION_WITH_ACTION,
      key,
      message: getSuccessNotificationNode(
        intl,
        intl?.formatMessage(routeActionMessages.duplicateSuccess) ?? "",
        () => {
          notification.close(key);
          showEntityChannel.put(openEntity({ entityId: route.id, entityType: EntityType.ROUTE }));
        }
      ),
    });
    yield put(duplicateRoute.success());
    yield put(notifyAboutChanges({ added: [route], entityType: EntityType.ROUTE }));
    yield call(callback);
  } catch (error) {
    notification.close(key);
    yield put(duplicateRoute.failure(error));
    yield put(handleError({ defaultMessage: routeActionMessages.duplicateFailure, error }));
  }
}

export function* routesSaga() {
  yield takeLatest(fetchRoutes.request, onFetchRoutes);
  yield takeEvery(fetchRoute.request, onFetchRoute);
  yield takeLatest(fetchRoutePreview.request, onFetchRoutePreview);
  yield takeEvery(deleteRoute.request, onDeleteRoute);
  yield takeEvery(duplicateRoute.request, onDuplicateRoute);
}
